import { useState } from 'react'
import { customerData, DependentInfo } from '../../../utilities/CustomerData'
import { apiURL } from '../../../utilities/URLs'
import { refreshAccessToken } from '../../../utilities/extensions'
import { useNavigate } from 'react-router-dom'

export function RefundInfo({ step, setStep }) {
  const navigate = useNavigate()

  const [refundStep, setRefundStep] = useState(0)
  const [checkingForDependents, setCheckingForDependents] = useState(false)
  const [dependentInfo, setDependentInfo] = useState(new DependentInfo())
  const [dependentsData, setDependentsData] = useState([])
  const [currentDependentIndex, setCurrentDependentIndex] = useState(0)
  const [uploadingDependentInfo, setUploadDependentInfo] = useState(false)

  const handleCheckForDependents = async () => {
    setCheckingForDependents(true)
    const form = new FormData()
    form.append('dependentInfo', JSON.stringify({
      customerId: customerData.customerLeadData.leadContact.contactId,
      providerId: customerData.customerLeadData.leadContact.chartInstantAccessToken || customerData.customerLeadData.leadContact.chartAccessToken || customerData.customerLeadData.leadContact.taxStatusId,
      providerName: (customerData.customerLeadData.leadContact.chartInstantAccessToken || customerData.customerLeadData.leadContact.chartAccessToken) ? 'Chart' : 'Tax Status',
      customerUF: customerData.customerDocuments.taxDocument2021UF
    }))

    const dependentsResponse = await fetch(`${apiURL}/api/checkDependents`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const dependentsData = await dependentsResponse.json()
    setCheckingForDependents(false)

    if (dependentsData.response.error) {
      if (dependentsData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleCheckForDependents() : navigate('/', { state: { expired: true } })
      } else
        console.error(dependentsData.response.error)
    } else {
      const dependentInfo = new DependentInfo(dependentsData.response.dependentInfo)
      setDependentInfo(dependentInfo)

      const initialDependentsData = Array(dependentInfo.numOfDependents).fill({
        firstName: '',
        lastName: '',
        dob: '',
        ssn: '',
        relationship: '',
        showDisabled: true,
        isDisabled: false,
        isQualified: true
      })
      setDependentsData(initialDependentsData)

      if (dependentInfo.hasDependents)
        setRefundStep(refundStep + 1)
      else
        setStep(step + 1)
    }
  }

  const handleDependentChange = (e, field) => {
    const value = e.target.value
  
    setDependentsData((prevData) => {
      const updatedDependents = [...prevData]
      const updatedDependent = {
        ...updatedDependents[currentDependentIndex],
        [field]: value,
      };
  
      if (field === 'dob') {
        const birthDate = new Date(value);
        const referenceDate = new Date('2021-12-31')
        const age = referenceDate.getFullYear() - birthDate.getFullYear() - (referenceDate < new Date(birthDate.setFullYear(referenceDate.getFullYear())) ? 1 : 0)
        updatedDependent.showDisabled = age >= 18
        updatedDependent.isQualified = age <= 17
      }
  
      updatedDependents[currentDependentIndex] = updatedDependent
      return updatedDependents
    })
  }

  const isCurrentDependentComplete = () => {
    const currentDependent = dependentsData[currentDependentIndex]
    return currentDependent?.firstName && currentDependent?.lastName && currentDependent?.dob && currentDependent?.ssn && currentDependent?.relationship
  }

  const handleCheckboxChange = (e, index) => {
    const updatedDependents = [...dependentsData]
    updatedDependents[index].isDisabled = e.target.checked
    setDependentsData(updatedDependents)
  }

  const handleUploadDependentInfo = async () => {
    setUploadDependentInfo(true)
    const form = new FormData()
    form.append('dependentInfo', JSON.stringify({
      leadId: customerData.customerLeadData.leadId,
      customerId: customerData.customerLeadData.leadContact.contactId,
      dependentsData,
      documentUF: customerData.customerDocuments.taxDocument2021UF,
      currentRefundAmount: customerData.customerLeadData.leadAmount,
      customerFirstName: customerData.customerLeadData.leadContact.contactFirstName,
      customerLastName: customerData.customerLeadData.leadContact.contactLastName
    }))
    const dependentInfoResponse = await fetch(`${apiURL}/api/uploadDependentInfo`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const dependentInfoData = await dependentInfoResponse.json()
    setUploadDependentInfo(false)
    if(dependentInfoData.response.error) {
      if(dependentInfoData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleUploadDependentInfo() : navigate('/', { state: { expired: true } })
      } else
        console.error(dependentInfoData.response.error)
    } else {
      customerData.customerLeadData.leadAmount = Number(dependentInfoData.response.refundAmount)
      customerData.customerDocuments.taxDocument2021 = String(dependentInfoData.response.flattenedPDFLink)
      customerData.customerDocuments.taxDocument2021UF = String(dependentInfoData.response.unflattenedPDFLink)
      setStep(step+1)
    }
  }

  return (
    <div className={`w-full flex flex-col items-center overflow-y-auto ${refundStep === 0 && 'h-[90vh] justify-center'}`}>
      {!checkingForDependents ? (
        refundStep === 0 ? (
          <div className='max-w-lg max-sm:w-11/12 flex flex-col items-center shadow-lg rounded-md p-8 gap-4'>
            <p className='text-lg font-semibold text-center'>Refund Calculated</p>
            <p className='text-center'>We've carefully reviewed and finalized your refund amount. Please click 'Next' to continue the process.</p>
            <p className='text-center'>Please click 'Next' to proceed.</p>
            <div className='flex justify-center w-full'>
              <button
                onClick={handleCheckForDependents}
                className='px-8 py-2 text-white rounded bg-primaryColor hover:bg-primaryDarkColor transition duration-200'>
                Next
              </button>
            </div>
          </div>
        ) : (
          <div className='w-full h-fit flex flex-col items-center gap-4 p-8 max-sm:mb-10'>
            <p className='text-lg font-semibold text-center'>Dependent Information</p>
            <p className='text-center'>Because you claimed one or more dependents, we need to collect their information to accurately prepare your tax documents. Please complete the information below.</p>
            <p className='text-center'>You have <span className='font-bold'>{dependentInfo.numOfDependents} dependents</span> to enter.</p>
            {dependentsData.length > 0 && (
              <div className='lg:w-1/2 w-full bg-white shadow-lg rounded-lg p-6 my-4'>
                <p className='text-xl font-semibold text-center text-gray-700'>Dependent {currentDependentIndex + 1}</p>
                {dependentInfo.ssns[currentDependentIndex] && (
                  <p className='text-center'>SSN ending in {dependentInfo.ssns[currentDependentIndex]}</p>
                )}
                <div className='mb-4'>
                  <label className='block text-sm font-medium text-gray-600'>First Name</label>
                  <input
                    type='text'
                    value={dependentsData[currentDependentIndex]?.firstName || ''}
                    onChange={(e) => handleDependentChange(e, 'firstName')}
                    placeholder='First Name'
                    className='mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primaryColor focus:border-primaryColor'
                  />
                </div>
                <div className='mb-4'>
                  <label className='block text-sm font-medium text-gray-600'>Last Name</label>
                  <input
                    type='text'
                    value={dependentsData[currentDependentIndex]?.lastName || ''}
                    onChange={(e) => handleDependentChange(e, 'lastName')}
                    placeholder='Last Name'
                    className='mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primaryColor focus:border-primaryColor'
                  />
                </div>
                <div className='mb-4'>
                  <label className='block text-sm font-medium text-gray-600'>Date of Birth</label>
                  <input
                    type='date'
                    value={dependentsData[currentDependentIndex]?.dob || ''}
                    onChange={(e) => handleDependentChange(e, 'dob')}
                    className='mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primaryColor focus:border-primaryColor'
                  />
                </div>
                {dependentsData[currentDependentIndex]?.showDisabled && (
                  <label className='flex items-center space-x-2 mb-4'>
                    <input
                      type='checkbox'
                      checked={dependentsData[currentDependentIndex]?.isDisabled || false}
                      onChange={(e) => handleCheckboxChange(e, currentDependentIndex)}
                      className='form-checkbox'
                    />
                    <span>Is the dependent disabled?</span>
                  </label>
                )}
                <div className='mb-4'>
                  <label className='block text-sm font-medium text-gray-600'>SSN</label>
                  <input
                    type='text'
                    value={dependentsData[currentDependentIndex]?.ssn || ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '')
                      if (value.length > 9) value = value.slice(0, 9)

                      const formattedSSN = value
                        .replace(/^(\d{3})(\d{0,2})(\d{0,4})$/, (_, p1, p2, p3) =>
                          [p1, p2, p3].filter(Boolean).join('-')
                        )

                      handleDependentChange({ target: { name: 'ssn', value: formattedSSN } }, 'ssn')
                    }}
                    placeholder='XXX-XX-XXXX'
                    className='mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primaryColor focus:border-primaryColor'
                  />
                </div>
                <div className='mb-4'>
                  <label className='block text-sm font-medium text-gray-600'>Relationship</label>
                  <select
                    value={dependentsData[currentDependentIndex]?.relationship || ''}
                    onChange={(e) => handleDependentChange(e, 'relationship')}
                    className='mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primaryColor focus:border-primaryColor'
                  >
                    <option value=''>Select Relationship</option>
                    <option value='Son'>Child</option>
                    <option value='Daughter'>Daughter</option>
                    <option value='Father'>Father</option>
                    <option value='Mother'>Mother</option>
                    <option value='Brother'>Brother</option>
                    <option value='Sister'>Sister</option>
                    <option value='Uncle'>Uncle</option>
                    <option value='Aunt'>Aunt</option>
                    <option value='Nephew'>Nephew</option>
                    <option value='Niece'>Niece</option>
                    <option value='Grandfather'>Grandfather</option>
                    <option value='Grandmother'>Grandmother</option>
                    <option value='Grandson'>Grandson</option>
                    <option value='Granddaughter'>Granddaughter</option>
                    <option value='Son-In-Law'>Son-In-Law</option>
                    <option value='Daughter-In-Law'>Daughter-In-Law</option>
                    <option value='Brother-In-Law'>Brother-In-Law</option>
                    <option value='Sister-In-Law'>Sister-In-Law</option>
                    <option value='Father-In-Law'>Father-In-Law</option>
                    <option value='Mother-In-Law'>Mother-In-Law</option>
                    <option value='Other'>Other</option>
                  </select>
                </div>

                <div className='flex justify-between mt-4'>
                  <button
                    disabled={currentDependentIndex === 0}
                    onClick={() => setCurrentDependentIndex((prev) => prev - 1)}
                    className='px-6 py-2 text-white rounded bg-gray-500 hover:bg-gray-600 transition duration-200 disabled:opacity-50'>
                    Previous
                  </button>
                  <button
                    disabled={!isCurrentDependentComplete() || uploadingDependentInfo}
                    onClick={() => currentDependentIndex < dependentsData.length - 1 ? setCurrentDependentIndex((prev) => prev + 1) : handleUploadDependentInfo()}
                    className={`px-6 py-2 text-white rounded ${
                      isCurrentDependentComplete() && !uploadingDependentInfo ? 'bg-primaryColor hover:bg-primaryDarkColor' : 'bg-gray-400 cursor-not-allowed'
                    } transition duration-200`}>
                    {currentDependentIndex < dependentsData.length - 1 ? 'Next' : 'Finish'}
                  </button>
                </div>
                {uploadingDependentInfo && (
                  <div className='flex flex-row items-center justify-center gap-2'>
                    <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
                    <p>Uploading Dependent Info...</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      ) : (
        <div className='flex flex-row items-center justify-center gap-2'>
          <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
          <p>Loading...</p>
        </div>
      )}
    </div>
  )
}
